import React from 'react';
import { Box, Typography } from '@mui/material';

// ── Inline renderer for a subset of Markdown ─────────────────────────────────
// Supports: # headings, **bold**, *italic*, `code`, ordered/unordered lists,
// and horizontal rules. No external dependency required.
// ─────────────────────────────────────────────────────────────────────────────

/** Renders inline tokens: **bold**, *italic*, `code` */
const renderInline = (str: string): React.ReactNode => {
  const parts = str.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((part) => {
    if (/^\*\*(.+)\*\*$/.test(part)) {
      return (
        <strong key={`b-${part}`} style={{ fontWeight: 700, color: '#111' }}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (/^\*(.+)\*$/.test(part)) {
      return <em key={`i-${part}`}>{part.slice(1, -1)}</em>;
    }
    if (/^`(.+)`$/.test(part)) {
      return (
        <code
          key={`c-${part}`}
          style={{
            backgroundColor: '#f0f0f0',
            padding: '1px 4px',
            borderRadius: '3px',
            fontFamily: 'monospace',
            fontSize: '0.8em',
          }}
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    return part;
  });
};

interface MarkdownRendererProps {
  text: string;
}

/** Renders a single heading, list item, hr, blank line, or paragraph line. */
const renderLine = (
  line: string,
  key: number,
  flushList: () => void,
  elements: React.ReactNode[],
  listBuffer: string[],
  listTypeRef: { current: 'ol' | 'ul' | null },
  keyRef: { current: number }
) => {
  if (/^###\s+/.test(line)) {
    flushList();
    elements.push(
      <Typography key={`h3-${keyRef.current++}`} sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#043B72', mt: 1.5, mb: 0.5 }}>
        {renderInline(line.replace(/^###\s+/, ''))}
      </Typography>
    );
  } else if (/^##\s+/.test(line)) {
    flushList();
    elements.push(
      <Typography key={`h2-${keyRef.current++}`} sx={{ fontWeight: 700, fontSize: '1rem', color: '#043B72', mt: 2, mb: 0.5, borderBottom: '1px solid #e0eaf4', pb: 0.3 }}>
        {renderInline(line.replace(/^##\s+/, ''))}
      </Typography>
    );
  } else if (/^#\s+/.test(line)) {
    flushList();
    elements.push(
      <Typography key={`h1-${keyRef.current++}`} sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#043B72', mt: 2, mb: 0.5 }}>
        {renderInline(line.replace(/^#\s+/, ''))}
      </Typography>
    );
  } else if (/^\d+[.)]\s+/.test(line)) {
    if (listTypeRef.current !== 'ol') { flushList(); listTypeRef.current = 'ol'; }
    listBuffer.push(line.replace(/^\d+[.)]\s+/, ''));
  } else if (/^[-*]\s+/.test(line)) {
    if (listTypeRef.current !== 'ul') { flushList(); listTypeRef.current = 'ul'; }
    listBuffer.push(line.replace(/^[-*]\s+/, ''));
  } else if (/^---+$/.test(line.trim())) {
    flushList();
    elements.push(
      <hr key={`hr-${keyRef.current++}`} style={{ border: 'none', borderTop: '1px solid #e0eaf4', margin: '10px 0' }} />
    );
  } else if (line.trim() === '') {
    flushList();
    elements.push(<Box key={`sp-${keyRef.current++}`} sx={{ height: '6px' }} />);
  } else {
    flushList();
    elements.push(
      <Typography key={`p-${keyRef.current++}`} variant="body2" sx={{ color: '#333333', lineHeight: 1.7, mb: 0.3 }}>
        {renderInline(line)}
      </Typography>
    );
  }
};

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ text }) => {
  if (!text) return null;

  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  const listBuffer: string[] = [];
  const listTypeRef = { current: null as 'ol' | 'ul' | null };
  const keyRef = { current: 0 };

  const flushList = () => {
    if (!listBuffer.length) return;
    const Tag = listTypeRef.current === 'ol' ? 'ol' : 'ul';
    elements.push(
      <Tag
        key={`list-${keyRef.current++}`}
        style={{
          margin: '6px 0 6px 20px',
          paddingLeft: '4px',
          color: '#333333',
          fontSize: '0.875rem',
          lineHeight: 1.7,
        }}
      >
        {listBuffer.map((item) => (
          <li key={item} style={{ marginBottom: '2px' }}>
            {renderInline(item)}
          </li>
        ))}
      </Tag>
    );
    listBuffer.length = 0;
    listTypeRef.current = null;
  };

  for (const line of lines) {
    renderLine(line, keyRef.current, flushList, elements, listBuffer, listTypeRef, keyRef);
  }

  flushList(); // flush any trailing list

  return <>{elements}</>;
};

export default MarkdownRenderer;
